/**
 * The publication calendar: days, weeks and the editorial date of a content.
 *
 * Days are the person's own (the browser's time zone): "domani alle 9" must
 * land on tomorrow's row for whoever reads it. Keys are `YYYY-MM-DD` so a day
 * can live in the URL (`?data=2026-09-28`). Pure, with dates passed in, so the
 * tests do not depend on the clock.
 *
 * The editorial date is the content's own field (RF-19, `editorialPublishDate`
 * in packages/domain/src/aggregates/content.ts): `publishedAt`, or
 * `PublishDate` for what was imported as is. A published content whose date
 * is in the future stays off the site until then.
 */

export type DayKey = string

const DAY_KEY = /^(\d{4})-(\d{2})-(\d{2})$/

function pad(value: number): string {
  return String(value).padStart(2, '0')
}

export function dayKeyOf(date: Date): DayKey {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

export function dayKey(timestamp: number): DayKey {
  return dayKeyOf(new Date(timestamp))
}

/** Local midnight of a day key, or `null` for anything that is not a real day. */
export function parseDayKey(key: string | null | undefined): Date | null {
  const match = key ? DAY_KEY.exec(key) : null
  if (!match) return null
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])]
  const date = new Date(year, month - 1, day)
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : null
}

export function addDays(key: DayKey, days: number): DayKey {
  const date = parseDayKey(key) ?? new Date()
  return dayKeyOf(new Date(date.getFullYear(), date.getMonth(), date.getDate() + days))
}

/** The Monday of the week a day belongs to: the Italian calendar starts on Monday. */
export function weekStart(key: DayKey): DayKey {
  const date = parseDayKey(key) ?? new Date()
  const offset = (date.getDay() + 6) % 7
  return dayKeyOf(new Date(date.getFullYear(), date.getMonth(), date.getDate() - offset))
}

export function weekDays(key: DayKey): DayKey[] {
  const start = weekStart(key)
  return Array.from({ length: 7 }, (_, index) => addDays(start, index))
}

export interface DayGroup<T> {
  day: DayKey
  items: T[]
}

/** Items grouped by local day, days in order, items by time within the day. */
export function groupByDay<T extends { publishedAt: number }>(items: readonly T[]): DayGroup<T>[] {
  const groups = new Map<DayKey, T[]>()
  for (const item of [...items].sort((a, b) => a.publishedAt - b.publishedAt)) {
    const key = dayKey(item.publishedAt)
    const list = groups.get(key)
    if (list) list.push(item)
    else groups.set(key, [item])
  }
  return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([day, list]) => ({ day, items: list }))
}

/** One entry per day of the week, empty days included. */
export function weekBuckets<T extends { publishedAt: number }>(items: readonly T[], anyDayOfWeek: DayKey): DayGroup<T>[] {
  const byDay = new Map(groupByDay(items).map((group) => [group.day, group.items]))
  return weekDays(anyDayOfWeek).map((day) => ({ day, items: byDay.get(day) ?? [] }))
}

// ---------------------------------------------------------------------------
// The editorial date of a content
// ---------------------------------------------------------------------------

export type EditorialDateKey = 'publishedAt' | 'PublishDate'

export interface EditorialDate {
  /** The field that holds it, or would hold it. */
  key: EditorialDateKey
  /** Null when there is no readable date. */
  value: number | null
  /** What the field holds today, to write the new date in the same form. */
  raw: unknown
}

function readDate(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const parsed = Date.parse(value)
    if (Number.isFinite(parsed)) return parsed
  }
  return null
}

/**
 * Where the date lives, read the way the gateway reads it: `publishedAt`
 * wins, `PublishDate` is the imported one. A content that has only
 * `PublishDate` keeps it there, so the change lands on the field it reads.
 */
export function editorialDateOf(data: Record<string, unknown>): EditorialDate {
  const own = data.publishedAt
  if (own !== undefined && own !== null) return { key: 'publishedAt', value: readDate(own), raw: own }
  const imported = data.PublishDate
  if (imported !== undefined && imported !== null) return { key: 'PublishDate', value: readDate(imported), raw: imported }
  return { key: 'publishedAt', value: null, raw: own }
}

/** The new date in the form the field already uses: a number stays a number, anything else becomes ISO 8601. */
export function editorialDateValue(timestamp: number, previousRaw: unknown): number | string {
  return typeof previousRaw === 'number' ? timestamp : new Date(timestamp).toISOString()
}

/** For `<input type="datetime-local">`, in the browser's time zone. */
export function toLocalInputValue(timestamp: number | null | undefined): string {
  if (timestamp === null || timestamp === undefined || !Number.isFinite(timestamp)) return ''
  const date = new Date(timestamp)
  return `${dayKeyOf(date)}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/** Back from `datetime-local`; `null` for an empty or unreadable value. */
export function fromLocalInputValue(value: string): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value.trim())
  if (!match) return null
  const [year, month, day, hours, minutes] = match.slice(1).map(Number) as [number, number, number, number, number]
  const date = new Date(year, month - 1, day, hours, minutes)
  return Number.isFinite(date.getTime()) ? date.getTime() : null
}

export type ScheduleState = 'none' | 'scheduled' | 'past' | 'waitingForPublish'

/**
 * What the date means for this content right now: scheduled (published, date
 * ahead), past (already public by date), waiting (a future date on a content
 * not yet marked published: nothing happens until it is), or no date.
 */
export function scheduleState(status: string, date: number | null, now: number): ScheduleState {
  if (date === null) return 'none'
  if (date <= now) return 'past'
  return status === 'published' ? 'scheduled' : 'waitingForPublish'
}
